import React, { useState, useEffect, useRef } from 'react';
import { createPaciente, createClinicalRecord, updatePaciente, updateClinicalRecord } from '../../servicios/apiClinica';
import { savePendingTransaction } from '../../servicios/offlineSync';
import Modal from '../../comun/Modal';
import logo from '../../assets/imagen_registro.png';
import html2canvas from 'html2canvas';
import Odontograma from './Odontograma';
import Fase789 from './Fase789';
import Fase1011 from './Fase1011';
import Fase12 from './Fase12';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FormularioPaciente = ({ onSave, onCancel, initialData = null, clinicalData = null, isViewOnly = false, quoteIdToComplete = null }) => {
    const [saveModal, setSaveModal] = useState({ show: false, message: '', type: 'online' });
    const user = JSON.parse(localStorage.getItem('user'));
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().substring(0, 5);
    const defaultProfessional = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '';

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        cedula: '',
        birth_date: '',
        representative: '',
        phone: '',
        occupation: '',
        email: '',
        address: '',
        age_group: '',
        is_pregnant: false,
        reason_for_consultation: '', 
        current_illness: '',
        allergies_antibiotic: false,
        allergies_anesthesia: false,
        hemorrhages: false,
        hiv_aids: false,
        tuberculosis: false,
        asthma: false,
        diabetes: false,
        hypertension: false,
        cardiac_disease: false,
        other_history: false,
        other_history_notes: '',
        personal_history_notes: '',
        family_history_notes: '',
        blood_pressure: '',
        heart_rate: '',
        temperature: '',
        respiratory_rate: '',
        stomatognathic_lips: false,
        stomatognathic_cheeks: false,
        stomatognathic_upper_jaw: false,
        stomatognathic_lower_jaw: false,
        stomatognathic_tongue: false,
        stomatognathic_palate: false,
        stomatognathic_floor: false,
        stomatognathic_carrillos: false,
        stomatognathic_salivary_glands: false,
        stomatognathic_oropharynx: false,
        stomatognathic_tmj: false,
        stomatognathic_lymph_nodes: false,
        stomatognathic_notes: '',
        odontogram_data: {},
        oral_health_indicators: {},
        periodontal_disease: '',
        malocclusion: '',
        fluorosis: '',
        cpo_ceo_indices: {},
        diagnostic_plans: {},
        diagnoses: [{}, {}, {}, {}],
        treatments: [{ date: today, time: currentTime }],
        opening_date: today,
        control_date: '',
        professional: 'Dra. Ximena Alexandra Lema Narváez',
        professional_code: '0302804828',
        sheet_number: '',
        consent: [{ ambulatorio: true }]
    });

    // Ref que siempre apunta al formData más reciente — evita closures obsoletas en handleSubmit
    const formDataRef = useRef(formData);
    useEffect(() => { formDataRef.current = formData; }, [formData]);

    useEffect(() => {
        if (initialData) {
            setFormData({
                first_name: initialData.first_name || '',
                last_name: initialData.last_name || '',
                cedula: initialData.cedula || '',
                birth_date: initialData.birth_date || '',
                gender: initialData.gender || '',
                representative: initialData.representative || '',
                phone: initialData.phone || '',
                occupation: initialData.occupation || '',
                email: initialData.email || '',
                address: initialData.address || '',
                age_group: initialData.age_group || '',
                is_pregnant: initialData.is_pregnant || false,
                reason_for_consultation: (clinicalData?.reason_for_consultation || '').replace(/^"\s*|\s*"$/g, ''),
                current_illness: clinicalData?.current_illness || '',
                allergies_antibiotic: clinicalData?.allergies_antibiotic || false,
                allergies_anesthesia: clinicalData?.allergies_anesthesia || false,
                hemorrhages: clinicalData?.hemorrhages || false,
                hiv_aids: clinicalData?.hiv_aids || false,
                tuberculosis: clinicalData?.tuberculosis || false,
                asthma: clinicalData?.asthma || false,
                diabetes: clinicalData?.diabetes || false,
                hypertension: clinicalData?.hypertension || false,
                cardiac_disease: clinicalData?.cardiac_disease || false,
                other_history: clinicalData?.other_history || false,
                other_history_notes: clinicalData?.other_history_notes || '',
                personal_history_notes: clinicalData?.personal_history_notes || '',
                family_history_notes: clinicalData?.family_history_notes || '',
                blood_pressure: clinicalData?.blood_pressure || '',
                heart_rate: clinicalData?.heart_rate || '',
                temperature: clinicalData?.temperature || '',
                respiratory_rate: clinicalData?.respiratory_rate || '',
                stomatognathic_lips: clinicalData?.stomatognathic_lips || false,
                stomatognathic_cheeks: clinicalData?.stomatognathic_cheeks || false,
                stomatognathic_upper_jaw: clinicalData?.stomatognathic_upper_jaw || false,
                stomatognathic_lower_jaw: clinicalData?.stomatognathic_lower_jaw || false,
                stomatognathic_tongue: clinicalData?.stomatognathic_tongue || false,
                stomatognathic_palate: clinicalData?.stomatognathic_palate || false,
                stomatognathic_floor: clinicalData?.stomatognathic_floor || false,
                stomatognathic_carrillos: clinicalData?.stomatognathic_carrillos || false,
                stomatognathic_salivary_glands: clinicalData?.stomatognathic_salivary_glands || false,
                stomatognathic_oropharynx: clinicalData?.stomatognathic_oropharynx || false,
                stomatognathic_tmj: clinicalData?.stomatognathic_tmj || false,
                stomatognathic_lymph_nodes: clinicalData?.stomatognathic_lymph_nodes || false,
                stomatognathic_notes: clinicalData?.stomatognathic_notes || '',
                odontogram_data: clinicalData?.odontogram_data || {},
                oral_health_indicators: clinicalData?.oral_health_indicators || {},
                periodontal_disease: clinicalData?.periodontal_disease || '',
                malocclusion: clinicalData?.malocclusion || '',
                fluorosis: clinicalData?.fluorosis || '',
                cpo_ceo_indices: clinicalData?.cpo_ceo_indices || {},
                diagnostic_plans: clinicalData?.diagnostic_plans || {},
                diagnoses: clinicalData?.diagnoses || [{}, {}, {}, {}],
                treatments: (() => {
                    const saved = clinicalData?.treatments || [];
                    // Cargar TODOS los tratamientos guardados sin filtrar
                    return saved.length > 0 ? saved : [{ date: today, time: currentTime }];
                })(),
                opening_date: clinicalData?.opening_date || today,
                control_date: clinicalData?.control_date || '',
                professional: clinicalData?.professional || 'Dra. Ximena Alexandra Lema Narváez',
                professional_code: clinicalData?.professional_code || '0302804828',
                sheet_number: clinicalData?.sheet_number || '',
                consent: (() => {
                    const c = clinicalData?.consent_data;
                    if (Array.isArray(c)) {
                        return c.length > 0 ? c : [{ ambulatorio: true }];
                    } else if (c && Object.keys(c).length > 0) {
                        return [c];
                    }
                    return [{ ambulatorio: true }];
                })()
            });
        }
    }, [initialData, clinicalData]);


    useEffect(() => {
        if (isViewOnly) return;
        
        const data = formData.odontogram_data;
        if (!data) return;

        let C = 0, P = 0, O = 0;
        let c = 0, e = 0, o = 0;

        const isPermanent = (id) => {
            const num = parseInt(id, 10);
            return (num >= 11 && num <= 18) || (num >= 21 && num <= 28) || (num >= 31 && num <= 38) || (num >= 41 && num <= 48);
        };

        const isPrimary = (id) => {
            const num = parseInt(id, 10);
            return (num >= 51 && num <= 55) || (num >= 61 && num <= 65) || (num >= 71 && num <= 75) || (num >= 81 && num <= 85);
        };

        Object.keys(data).forEach(id => {
            const t = data[id];
            if (!t) return;
            
            const isLost = t.status === 'red-x' || t.status === 'blue-x' || t.status === 'loss' || t.status === 'blue-loss';
            
            // All other red symbols imply pathology / required treatment
            const isRedStatus = t.status === 'red-circle' || t.status === 'crown' || t.status === 'total_prosthesis' || t.status === 'removable_prosthesis' || t.status === 'endodontics' || t.status === 'red-asterisk';
            const hasCaries = t.top === 'red' || t.bottom === 'red' || t.left === 'red' || t.right === 'red' || t.center === 'red' || isRedStatus;
            
            // All other blue symbols imply completed treatment
            const isBlueStatus = t.status === 'blue-circle' || t.status === 'blue-crown' || t.status === 'blue-total_prosthesis' || t.status === 'blue-removable_prosthesis' || t.status === 'blue-endodontics' || t.status === 'blue-asterisk';
            const hasFilling = t.top === 'blue' || t.bottom === 'blue' || t.left === 'blue' || t.right === 'blue' || t.center === 'blue' || isBlueStatus;

            if (isPermanent(id)) {
                if (isLost) P++;
                else if (hasCaries) C++;
                else if (hasFilling) O++;
            } else if (isPrimary(id)) {
                if (isLost) e++;
                else if (hasCaries) c++;
                else if (hasFilling) o++;
            }
        });

        const newIndices = {
            D: { c: C.toString(), p: P.toString(), o: O.toString(), total: (C + P + O).toString() },
            d: { c: c.toString(), e: e.toString(), o: o.toString(), total: (c + e + o).toString() }
        };

        const current = formData.cpo_ceo_indices || {};
        if (
            current.D?.c !== newIndices.D.c || current.D?.p !== newIndices.D.p || current.D?.o !== newIndices.D.o ||
            current.d?.c !== newIndices.d.c || current.d?.e !== newIndices.d.e || current.d?.o !== newIndices.d.o
        ) {
            setFormData(prev => ({
                ...prev,
                cpo_ceo_indices: {
                    ...prev.cpo_ceo_indices,
                    D: { ...prev.cpo_ceo_indices?.D, ...newIndices.D },
                    d: { ...prev.cpo_ceo_indices?.d, ...newIndices.d }
                }
            }));
        }
    }, [formData.odontogram_data, isViewOnly]);
    
    const [loading, setLoading] = useState(false);

    const calculateAgeGroup = (birthDate) => {
        if (!birthDate) return '';
        const dob = new Date(birthDate);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
        }

        if (age < 1) return '<1';
        if (age >= 1 && age <= 4) return '1-4';
        if (age >= 5 && age <= 9) return '5-9';
        if (age >= 10 && age <= 14) return '10-14';
        if (age >= 15 && age <= 19) return '15-19';
        if (age >= 20) return '>20';
        return '';
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => {
            let finalValue = type === 'checkbox' ? checked : value;
            if (typeof finalValue === 'string' && (name === 'first_name' || name === 'last_name' || name === 'representative')) {
                finalValue = finalValue.toUpperCase();
            }

            const newData = {
                ...prev,
                [name]: finalValue
            };
            if (name === 'birth_date') {
                newData.age_group = calculateAgeGroup(value);
            }
            return newData;
        });
    };

    const handleMotivoBlur = (e) => {
        let val = e.target.value.replace(/"/g, '').trim();
        setFormData(prev => ({
            ...prev,
            reason_for_consultation: val ? `"${val}"` : ''
        }));
    };

    const ageGroups = [
        { id: '<1', label: 'MENOR DE 1 AÑO', width: '6%' },
        { id: '1-4', label: '1 - 4 AÑOS', width: '6%' },
        { id: '5-9', label: '5-9 AÑOS PROGRAMADO', width: '10%' },
        { id: '5-14', label: '5-14 AÑOS NO PROGRAMADO', width: '11%' },
        { id: '10-14', label: '10-14 AÑOS PROGRAMADO', width: '11%' },
        { id: '15-19', label: '15 - 19 AÑOS', width: '9%' },
        { id: '>20', label: 'MAYOR DE 20 AÑOS', width: '11%' }
    ];

    const exportToWord = async () => {
        // Función para obtener la imagen en base64 y que Word la incruste directamente
        const getBase64ImageFromUrl = async (imageUrl) => {
            try {
                const response = await fetch(imageUrl);
                const blob = await response.blob();
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = () => resolve(null);
                    reader.readAsDataURL(blob);
                });
            } catch (e) {
                return null;
            }
        };

        const logoBase64 = await getBase64ImageFromUrl(window.location.origin + '/biodentality.png');
        const imgTag = logoBase64 ? `<img src="top_logo_image" width="110" height="90" style="width: 110px; height: 90px; object-fit: contain;" />` : `<h2 style="color: #4a5568;">Lemnardental</h2>`;

        // CONSTRUCCIÓN MANUAL DE FASE 1 A 3 (TABLAS CONTIGUAS PARA INDEPENDIZAR ANCHOS EN WORD)
        const customTopHtml = `
            <div align="center" style="margin-bottom: 10px;">
                ${imgTag}
            </div>
            
            <!-- DATOS PERSONALES -->
            <table border="1" cellpadding="1" cellspacing="0" width="100%" style="border-collapse: collapse; font-family: Calibri, sans-serif; font-size: 8pt; width: 100%; margin-bottom: 0;">
                <tr>
                    <td width="40%" valign="top"><div style="padding: 2px;"><b>Nombres y Apellidos:</b><br/>${(formData.first_name || '') + ' ' + (formData.last_name || '')}</div></td>
                    <td width="25%" valign="top"><div style="padding: 2px;"><b>Cédula/HCL:</b><br/>${formData.cedula || ''}</div></td>
                    <td width="35%" valign="top"><div style="padding: 2px;">
                        <b>Fecha nac.:</b> ${formData.birth_date ? formData.birth_date.split('-').reverse().join('/') : ''}<br/>
                        <b>Sexo:</b> ${formData.gender === 'M' ? 'Masculino' : formData.gender === 'F' ? 'Femenino' : ''}
                    </div></td>
                </tr>
                <tr>
                    <td width="40%" valign="top"><div style="padding: 2px;"><b>Representante:</b><br/>${formData.representative || ''}</div></td>
                    <td width="25%" valign="top"><div style="padding: 2px;"><b>Cédula Rep.:</b><br/>${formData.representative_cedula || ''}</div></td>
                    <td width="35%" valign="top"><div style="padding: 2px;"><b>Teléfono:</b><br/>${formData.phone || ''}</div></td>
                </tr>
                <tr>
                    <td width="40%" valign="top"><div style="padding: 2px;"><b>Dirección:</b><br/>${formData.address || ''}</div></td>
                    <td width="25%" valign="top"><div style="padding: 2px;"><b>Correo:</b><br/>${formData.email || ''}</div></td>
                    <td width="35%" valign="top"><div style="padding: 2px;"><b>Ocupación:</b><br/>${formData.occupation || ''}</div></td>
                </tr>
            </table>

            <!-- GRUPOS DE EDAD -->
            <table border="1" cellpadding="1" cellspacing="0" width="100%" style="border-collapse: collapse; font-family: Calibri, sans-serif; width: 100%; margin-bottom: 0; border-top: none;">
                <tr style="text-align: center;">
                    <td width="6%" style="border-top: none; font-size: 8pt;">MENOR DE<br/>1 AÑO</td><td width="2%" style="border-top: none;"><span style="font-size: 10pt;">${formData.age_group === '<1' ? '&#x25CF;' : '&#x25CB;'}</span></td>
                    <td width="6%" style="border-top: none; font-size: 8pt;">1 - 4<br/>AÑOS</td><td width="2%" style="border-top: none;"><span style="font-size: 10pt;">${formData.age_group === '1-4' ? '&#x25CF;' : '&#x25CB;'}</span></td>
                    <td width="10%" style="border-top: none; font-size: 8pt;">5-9 AÑOS<br/>PROGRAM.</td><td width="2%" style="border-top: none;"><span style="font-size: 10pt;">${formData.age_group === '5-9' ? '&#x25CF;' : '&#x25CB;'}</span></td>
                    <td width="11%" style="border-top: none; font-size: 8pt;">5-14 AÑOS<br/>NO PROG.</td><td width="2%" style="border-top: none;"><span style="font-size: 10pt;">${formData.age_group === '5-14' ? '&#x25CF;' : '&#x25CB;'}</span></td>
                    <td width="11%" style="border-top: none; font-size: 8pt;">10-14 AÑOS<br/>PROGRAM.</td><td width="2%" style="border-top: none;"><span style="font-size: 10pt;">${formData.age_group === '10-14' ? '&#x25CF;' : '&#x25CB;'}</span></td>
                    <td width="9%" style="border-top: none; font-size: 8pt;">15 - 19<br/>AÑOS</td><td width="2%" style="border-top: none;"><span style="font-size: 10pt;">${formData.age_group === '15-19' ? '&#x25CF;' : '&#x25CB;'}</span></td>
                    <td width="11%" style="border-top: none; font-size: 8pt;">MAYOR DE<br/>20 AÑOS</td><td width="2%" style="border-top: none;"><span style="font-size: 10pt;">${formData.age_group === '>20' ? '&#x25CF;' : '&#x25CB;'}</span></td>
                    <td width="9%" style="border-top: none; font-size: 8pt;">EMBARA-<br/>ZADA</td><td width="2%" style="border-top: none;"><span style="font-size: 10pt;">${formData.is_pregnant ? '&#x2612;' : '&#x2610;'}</span></td>
                    <td width="9%" style="border-top: none; font-size: 8pt;">DISCAPA-<br/>CIDAD</td><td width="2%" style="border-top: none;"><span style="font-size: 10pt;">&nbsp;</span></td>
                </tr>
            </table>

            <!-- MOTIVO DE CONSULTA -->
            <table border="1" cellpadding="1" cellspacing="0" width="100%" style="border-collapse: collapse; font-family: Calibri, sans-serif; font-size: 8pt; width: 100%; margin-bottom: 0; border-top: none;">
                <tr>
                    <td width="50%" style="background-color: #e6e6e6; border-bottom: none; border-top: none; border-right: none;"><div style="padding: 2px;"><b>1 MOTIVO DE CONSULTA</b></div></td>
                    <td width="50%" style="background-color: #e6e6e6; text-align: right; font-size: 5pt; border-bottom: none; border-top: none; border-left: none;"><div style="padding: 2px;">ANOTAR LA CAUSA DEL PROBLEMA EN LA VERSIÓN DEL INFORMANTE</div></td>
                </tr>
                <tr>
                    <td colspan="2" width="100%" style="height: 40px; border-top: none;" valign="top"><div style="padding: 2px;">${(formData.reason_for_consultation || '').replace(/\n/g, '<br/>')}</div></td>
                </tr>
            </table>

            <!-- ENFERMEDAD O PROBLEMA ACTUAL -->
            <table border="1" cellpadding="1" cellspacing="0" width="100%" style="border-collapse: collapse; font-family: Calibri, sans-serif; font-size: 8pt; width: 100%; margin-bottom: 0; border-top: none;">
                <tr>
                    <td width="30%" style="background-color: #e6e6e6; border-bottom: none; border-top: none; border-right: none;"><div style="padding: 2px;"><b>2 ENFERMEDAD O PROBLEMA ACTUAL</b></div></td>
                    <td width="70%" style="background-color: #e6e6e6; text-align: right; font-size: 5pt; border-bottom: none; border-top: none; border-left: none;"><div style="padding: 2px;">REGISTRAR SÍNTOMAS: CRONOLOGÍA, LOCALIZACIÓN, CARACTERÍSTICAS, INTENSIDAD, CAUSA APARENTE, SÍNTOMAS ASOCIADOS, EVOLUCIÓN, ESTADO ACTUAL</div></td>
                </tr>
                <tr>
                    <td colspan="2" width="100%" style="height: 40px; border-top: none;" valign="top"><div style="padding: 2px;">${(formData.current_illness || '').replace(/\n/g, '<br/>')}</div></td>
                </tr>
            </table>

            <!-- ANTECEDENTES PERSONALES Y FAMILIARES -->
            <table border="1" cellpadding="1" cellspacing="0" width="100%" style="border-collapse: collapse; font-family: Calibri, sans-serif; font-size: 8pt; width: 100%; margin-bottom: 0; border-top: none;">
                <tr>
                    <td colspan="20" width="100%" style="background-color: #e6e6e6; border-top: none;"><div style="padding: 2px;"><b>3 ANTECEDENTES PERSONALES Y FAMILIARES</b></div></td>
                </tr>
                <tr style="text-align: center;">
                    <td width="7%" style="font-size: 8pt;">1. ALERGIA<br/>ANTIBIÓT.</td><td width="3%"><span style="font-size: 10pt;">${formData.allergies_antibiotic ? '&#x2612;' : '&#x2610;'}</span></td>
                    <td width="7%" style="font-size: 8pt;">2. ALERGIA<br/>ANESTESIA</td><td width="3%"><span style="font-size: 10pt;">${formData.allergies_anesthesia ? '&#x2612;' : '&#x2610;'}</span></td>
                    <td width="7%" style="font-size: 8pt;">3. HEMO-<br/>RRAGIAS</td><td width="3%"><span style="font-size: 10pt;">${formData.hemorrhages ? '&#x2612;' : '&#x2610;'}</span></td>
                    <td width="7%" style="font-size: 8pt;">4. VIH/<br/>SIDA</td><td width="3%"><span style="font-size: 10pt;">${formData.hiv_aids ? '&#x2612;' : '&#x2610;'}</span></td>
                    <td width="7%" style="font-size: 8pt;">5. TUBER-<br/>CULOSIS</td><td width="3%"><span style="font-size: 10pt;">${formData.tuberculosis ? '&#x2612;' : '&#x2610;'}</span></td>
                    <td width="7%" style="font-size: 8pt;">6. ASMA</td><td width="3%"><span style="font-size: 10pt;">${formData.asthma ? '&#x2612;' : '&#x2610;'}</span></td>
                    <td width="7%" style="font-size: 8pt;">7. DIABE-<br/>TES</td><td width="3%"><span style="font-size: 10pt;">${formData.diabetes ? '&#x2612;' : '&#x2610;'}</span></td>
                    <td width="7%" style="font-size: 8pt;">8. HIPER-<br/>TENSIÓN</td><td width="3%"><span style="font-size: 10pt;">${formData.hypertension ? '&#x2612;' : '&#x2610;'}</span></td>
                    <td width="7%" style="font-size: 8pt;">9. ENF.<br/>CARDIACA</td><td width="3%"><span style="font-size: 10pt;">${formData.cardiac_disease ? '&#x2612;' : '&#x2610;'}</span></td>
                    <td width="7%" style="font-size: 8pt;">10. OTRO</td><td width="3%"><span style="font-size: 10pt;">${formData.other_history ? '&#x2612;' : '&#x2610;'}</span></td>
                </tr>
                <tr>
                    <td colspan="20" width="100%" style="height: 35px;" valign="top"><div style="padding: 2px;"><b>AP:</b><br/>${(formData.personal_history_notes || '').replace(/\n/g, '<br/>')}</div></td>
                </tr>
                <tr>
                    <td colspan="20" width="100%" style="height: 35px;" valign="top"><div style="padding: 2px;"><b>AF:</b><br/>${(formData.family_history_notes || '').replace(/\n/g, '<br/>')}</div></td>
                </tr>
            </table>
        `;

        // Clonar el documento para no alterar la vista actual (usaremos esto para la fase 4 en adelante)
        const originalDiv = document.getElementById("documento-impresion");
        const clone = originalDiv.cloneNode(true);
        
        // Eliminar botones si se clonaron
        const controls = clone.querySelectorAll('.d-print-none');
        controls.forEach(c => c.remove());

        // Reemplazar inputs de texto, email y date por spans con su valor
        const textInputs = clone.querySelectorAll('input[type="text"], input[type="email"], input[type="date"], input[type="number"]');
        const origInputs = originalDiv.querySelectorAll('input[type="text"], input[type="email"], input[type="date"], input[type="number"]');
        textInputs.forEach((input, index) => {
            const span = document.createElement('span');
            const val = origInputs[index].value || '';
            span.innerHTML = val ? `&nbsp;${val}&nbsp;` : '&nbsp;';
            const isOralInput = input.classList.contains('oral-input');
            span.style.fontFamily = 'Calibri, sans-serif';
            span.style.fontWeight = 'normal';
            span.style.fontSize = isOralInput ? '8pt' : '10pt';
            span.style.display = 'inline-block';
            if (!isOralInput) {
                span.style.minHeight = '14px'; 
            }
            span.style.verticalAlign = 'top';
            input.parentNode.replaceChild(span, input);
        });

        const textareas = clone.querySelectorAll('textarea');
        const origTextareas = originalDiv.querySelectorAll('textarea');
        textareas.forEach((ta, index) => {
            const val = origTextareas[index].value || '';
            const div = document.createElement('div');
            div.innerText = val;
            div.style.fontFamily = 'Calibri, sans-serif';
            div.style.fontSize = '10pt';
            div.style.padding = '10px 0';
            ta.parentNode.replaceChild(div, ta);
        });

        const checkInputs = clone.querySelectorAll('input[type="radio"], input[type="checkbox"]');
        const origChecks = originalDiv.querySelectorAll('input[type="radio"], input[type="checkbox"]');
        checkInputs.forEach((input, index) => {
            const checked = origChecks[index].checked;
            const span = document.createElement('span');
            span.innerHTML = checked ? '<b>&#10003;</b>' : '&nbsp;';
            span.style.fontFamily = 'Calibri, sans-serif';
            span.style.fontSize = '10pt';
            span.style.display = 'inline-block';
            if (input.classList.contains('checkbox-inline')) {
                span.style.width = 'auto';
                span.style.paddingLeft = '5px';
            } else {
                span.style.width = '100%';
                span.style.textAlign = 'center';
            }
            input.parentNode.replaceChild(span, input);
        });

        // Removed code that stripped background colors so cells retain their color in Word

        // Eliminar las partes del clon que ya hemos hecho manualmente (Header, Phase 1, 2, 3)
        // Buscaremos "4 SIGNOS VITALES" y borraremos todo lo anterior
        let foundPhase4 = false;
        const childrenToRemove = [];
        for (let i = 0; i < clone.children.length; i++) {
            const child = clone.children[i];
            if (child.innerText && child.innerText.includes('4 SIGNOS VITALES')) {
                foundPhase4 = true;
            }
            if (!foundPhase4) {
                childrenToRemove.push(child);
            }
        }
        childrenToRemove.forEach(c => c.remove());

        // Capture Odontograma as image
        const odontogramSource = document.getElementById('odontogram-export-container');
        if (odontogramSource) {
            try {
                // Ensure Odontogram container doesn't have overflow hidden/auto during capture
                const originalOverflow = odontogramSource.style.overflowX;
                odontogramSource.style.overflowX = 'visible';
                
                const canvas = await html2canvas(odontogramSource, { scale: 2, useCORS: true });
                const imgData = canvas.toDataURL('image/png');
                
                odontogramSource.style.overflowX = originalOverflow;
                
                const clonedOdontogramContainer = clone.querySelector('#odontogram-export-container');
                if (clonedOdontogramContainer) {
                    clonedOdontogramContainer.innerHTML = `<div style="text-align:center; padding: 5px 0;"><img src="${imgData}" width="480" height="240" /></div>`;
                }
            } catch (err) {
                console.error("Error capturando odontograma", err);
            }
        }

        // Build MHTML parts
        const boundary = "----=_NextPart_000_0000";
        let mhtml = `MIME-Version: 1.0\r\nContent-Type: multipart/related; boundary="${boundary}"\r\n\r\n`;
        const imageParts = [];
        let imageCounter = 0;
        
        // Add logo to MHTML parts
        if (logoBase64) {
            const matches = logoBase64.match(/^data:(image\/\w+);base64,(.*)$/);
            if (matches) {
                imageParts.push(
                    `--${boundary}\r\n` +
                    `Content-Type: ${matches[1]}\r\n` +
                    `Content-Transfer-Encoding: base64\r\n` +
                    `Content-Location: top_logo_image\r\n\r\n` +
                    `${matches[2]}\r\n`
                );
            }
        }

        // Convert all external images to base64 and collect them for MHTML
        const images = Array.from(clone.querySelectorAll('img'));
        for (const img of images) {
            let base64data = null;
            let mimeType = 'image/png';
            
            if (img.src && img.src.startsWith('data:')) {
                const matches = img.src.match(/^data:(image\/\w+);base64,(.*)$/);
                if (matches) {
                    mimeType = matches[1];
                    base64data = matches[2];
                }
            } else if (img.src) {
                try {
                    const response = await fetch(img.src);
                    const blob = await response.blob();
                    mimeType = blob.type || 'image/png';
                    if (!mimeType.startsWith('image/')) mimeType = 'image/png';
                    const reader = new FileReader();
                    base64data = await new Promise(resolve => {
                        reader.onloadend = () => {
                            const result = reader.result;
                            const matches = result.match(/^data:(image\/\w+);base64,(.*)$/);
                            resolve(matches ? matches[2] : null);
                        };
                        reader.readAsDataURL(blob);
                    });
                } catch (err) {
                    console.error("Error fetching image for Word export:", img.src, err);
                }
                
                if (!base64data) {
                    try {
                        const origImg = Array.from(document.querySelectorAll('img')).find(i => i.src === img.src);
                        if (origImg) {
                            const canvas = document.createElement('canvas');
                            canvas.width = origImg.naturalWidth || origImg.width || 150;
                            canvas.height = origImg.naturalHeight || origImg.height || 100;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(origImg, 0, 0);
                            const dataUrl = canvas.toDataURL('image/png');
                            const matches = dataUrl.match(/^data:(image\/\w+);base64,(.*)$/);
                            if (matches) {
                                mimeType = matches[1];
                                base64data = matches[2];
                            }
                        }
                    } catch (canvasErr) {
                        console.error("Canvas fallback failed for", img.src, canvasErr);
                    }
                }
            }
            
            if (base64data) {
                const contentLocation = `image${imageCounter}`;
                img.src = contentLocation;
                imageParts.push(
                    `--${boundary}\r\n` +
                    `Content-Type: ${mimeType}\r\n` +
                    `Content-Transfer-Encoding: base64\r\n` +
                    `Content-Location: ${contentLocation}\r\n\r\n` +
                    `${base64data}\r\n`
                );
                imageCounter++;
            }
        }

        const remainingHtml = clone.innerHTML;
        
        let completeHtml = `
            <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
            <head>
                <meta charset='utf-8'>
                <title>Registro Paciente</title>
                <style>
                    @page WordSection1 { 
                        size: 21cm 29.7cm; 
                        margin: 1cm 1cm 1.5cm 1cm; 
                        mso-footer: f1;
                    }
                    div.WordSection1 { page: WordSection1; }
                    body { font-family: Calibri, sans-serif; font-size: 10pt; }
                    table { border-collapse: collapse; width: 100%; border: 1px solid black; }
                    td, th { border: 1px solid black; padding: 5px; vertical-align: middle; font-family: Calibri, sans-serif; font-size: 10pt;}
                    .no-border-table { border: none !important; }
                    .no-border-table td { border: none !important; padding: 0 !important; }
                    .age-group-text { font-size: 5pt !important; }
                    .fw-bold { font-weight: bold; }
                    .d-flex { display: block; } /* Fix flex for Word */
                    br.d-print-page-break { mso-special-character: line-break; page-break-before: always; }
                    
                    /* Footer styles */
                    p.MsoFooter, li.MsoFooter, div.MsoFooter {
                        margin: 0;
                        font-size: 10pt;
                        font-family: Calibri, sans-serif;
                    }
                </style>
            </head>
            <body>
                <div class="WordSection1">
                    ${customTopHtml}
                    ${remainingHtml}
                </div>
            </body>
            </html>
        `;
        
        // Ensure CRLF
        completeHtml = completeHtml.replace(/\n/g, '\r\n');
        
        // Build final MHTML
        mhtml += `--${boundary}\r\n` +
                 `Content-Type: text/html; charset="utf-8"\r\n` +
                 `Content-Transfer-Encoding: 8bit\r\n\r\n` +
                 `${completeHtml}\r\n\r\n`;
                 
        if (imageParts.length > 0) {
            mhtml += imageParts.join('\r\n') + '\r\n';
        }
        mhtml += `--${boundary}--\r\n`;
        
        const blob = new Blob([mhtml], { type: 'application/msword' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Registro_${formData.first_name || 'Paciente'}.doc`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const patientData = {
                first_name: formData.first_name || '',
                last_name: formData.last_name || '',
                // Vacío -> el backend lo guarda como null. Nunca usar una cédula falsa: es única y choca con otros pacientes.
                cedula: formData.cedula || '',
                birth_date: formData.birth_date || null,
                gender: formData.gender || null,
                representative: formData.representative,
                phone: formData.phone,
                occupation: formData.occupation,
                email: formData.email,
                address: formData.address,
                age_group: formData.age_group,
                is_pregnant: formData.is_pregnant,
            };

            const recordData = {
                reason_for_consultation: formData.reason_for_consultation,
                current_illness: formData.current_illness,
                allergies_antibiotic: formData.allergies_antibiotic,
                allergies_anesthesia: formData.allergies_anesthesia,
                hemorrhages: formData.hemorrhages,
                hiv_aids: formData.hiv_aids,
                tuberculosis: formData.tuberculosis,
                asthma: formData.asthma,
                diabetes: formData.diabetes,
                hypertension: formData.hypertension,
                cardiac_disease: formData.cardiac_disease,
                other_history: formData.other_history,
                other_history_notes: formData.other_history_notes,
                personal_history_notes: formData.personal_history_notes,
                family_history_notes: formData.family_history_notes,
                blood_pressure: formData.blood_pressure,
                heart_rate: formData.heart_rate,
                temperature: formData.temperature,
                respiratory_rate: formData.respiratory_rate,
                stomatognathic_lips: formData.stomatognathic_lips,
                stomatognathic_cheeks: formData.stomatognathic_cheeks,
                stomatognathic_upper_jaw: formData.stomatognathic_upper_jaw,
                stomatognathic_lower_jaw: formData.stomatognathic_lower_jaw,
                stomatognathic_tongue: formData.stomatognathic_tongue,
                stomatognathic_palate: formData.stomatognathic_palate,
                stomatognathic_floor: formData.stomatognathic_floor,
                stomatognathic_carrillos: formData.stomatognathic_carrillos,
                stomatognathic_salivary_glands: formData.stomatognathic_salivary_glands,
                stomatognathic_oropharynx: formData.stomatognathic_oropharynx,
                stomatognathic_tmj: formData.stomatognathic_tmj,
                stomatognathic_lymph_nodes: formData.stomatognathic_lymph_nodes,
                stomatognathic_notes: formData.stomatognathic_notes,
                odontogram_data: formData.odontogram_data,
                oral_health_indicators: formData.oral_health_indicators,
                periodontal_disease: formData.periodontal_disease,
                malocclusion: formData.malocclusion,
                fluorosis: formData.fluorosis,
                cpo_ceo_indices: formData.cpo_ceo_indices,
                diagnostic_plans: formData.diagnostic_plans,
                diagnoses: formData.diagnoses,
                treatments: formDataRef.current.treatments,
                consent_data: formDataRef.current.consent || {}
            };

        // Id del paciente ya guardado en el servidor (si falla el registro clínico después, no se duplica)
        let savedPatientId = initialData?.id || null;

        try {
            const autoCreateAppointment = async (patientId) => {
                // A petición del usuario, ya no se generan citas automáticamente desde la Fase 12.
                return;
                const firstTreatment = recordData.treatments?.[0];
                if (firstTreatment && firstTreatment.date && firstTreatment.time && !firstTreatment.appointment_id) {
                    try {
                        const token = localStorage.getItem('token');
                        const startStr = `${firstTreatment.time}:00`;
                        let [h, m] = firstTreatment.time.split(':').map(Number);
                        m += 30;
                        if (m >= 60) { h++; m -= 60; }
                        const endStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;

                        const srvIds = Array.isArray(firstTreatment.service_ids) && firstTreatment.service_ids.length > 0
                            ? firstTreatment.service_ids
                            : (firstTreatment.service_id ? [firstTreatment.service_id] : []);
                        
                        let totalPrice = 0;
                        let servicesData = [];
                        let allTreatments = [];

                        try {
                            const trListRes = await fetch(`${API_URL}/api/treatments/?limit=1000`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (trListRes.ok) {
                                const tData = await trListRes.json();
                                allTreatments = tData.results || tData || [];
                            }
                        } catch(e) {}

                        for (const sId of srvIds) {
                            const qPriceObj = firstTreatment.quote_prices?.find(q => String(q.name).trim().toLowerCase() === String(sId).trim().toLowerCase());
                            const foundSrv = allTreatments.find(t => String(t.id) === String(sId) || String(t.name).trim().toLowerCase() === String(sId).trim().toLowerCase());
                            
                            const price = qPriceObj ? parseFloat(qPriceObj.price || 0) : (foundSrv ? parseFloat(foundSrv.price || 0) : 0);
                            const discount = qPriceObj ? parseFloat(qPriceObj.discount || 0) : 0;
                            
                            if (foundSrv || qPriceObj) {
                                totalPrice += price;
                                servicesData.push({
                                    treatment: foundSrv ? foundSrv.id : null,
                                    name: foundSrv ? foundSrv.name : String(sId),
                                    price: price,
                                    doctor: null,
                                    notes: firstTreatment.procedures || ''
                                });
                                
                                if (discount > 0) {
                                    totalPrice -= discount;
                                    servicesData.push({
                                        treatment: null,
                                        name: 'Descuento (Cotización)',
                                        price: -Math.abs(discount),
                                        doctor: null,
                                        notes: `Aplicado a ${String(sId)}`
                                    });
                                }
                            } else {
                                servicesData.push({
                                    treatment: null,
                                    name: String(sId),
                                    price: 0,
                                    doctor: null,
                                    notes: firstTreatment.procedures || ''
                                });
                            }
                        }

                        const aptData = {
                            patient: patientId,
                            date: firstTreatment.date,
                            start_time: startStr,
                            end_time: endStr,
                            treatment: servicesData.length > 0 ? servicesData[0].treatment : null,
                            status: 'completed',
                            notes: 'Generada automáticamente al crear historia clínica',
                            services_data: servicesData
                        };
                        
                        const aptRes = await fetch(`${API_URL}/api/appointments/`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify(aptData)
                        });
                        
                        if (aptRes.ok) {
                            const newApt = await aptRes.json();
                            firstTreatment.appointment_id = newApt.id;
                            
                            // Add debt to patient's balance
                            if (totalPrice > 0) {
                                try {
                                    const pRes = await fetch(`${API_URL}/api/patients/${patientId}/`, {
                                        headers: { 'Authorization': `Bearer ${token}` }
                                    });
                                    if (pRes.ok) {
                                        const pData = await pRes.json();
                                        const currentBalance = parseFloat(pData.balance || 0);
                                        await fetch(`${API_URL}/api/patients/${patientId}/`, {
                                            method: 'PATCH',
                                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                            body: JSON.stringify({ balance: currentBalance + totalPrice })
                                        });
                                    }
                                } catch(e) { console.error('Error adding debt to patient', e); }
                            }
                        }
                    } catch (e) { console.error('Error auto-creating appointment', e); }
                }
            };

            if (!navigator.onLine) {
                throw new Error("Offline mode");
            }

            if (initialData && initialData.id) {
                // Update mode
                await updatePaciente(initialData.id, patientData);
                
                await autoCreateAppointment(initialData.id);
                
                if (clinicalData && clinicalData.id) {
                    await updateClinicalRecord(clinicalData.id, recordData);
                } else {
                    await createClinicalRecord({ ...recordData, patient: initialData.id });
                }
            } else {
                // Create mode
                const createdPatient = await createPaciente(patientData);
                if (createdPatient?.id) savedPatientId = createdPatient.id;
                const targetPatientId = clinicalData && clinicalData.id ? initialData.id : (createdPatient ? createdPatient.id : initialData?.id);
                
                await autoCreateAppointment(targetPatientId);

                if (clinicalData && clinicalData.id) {
                    await updateClinicalRecord(clinicalData.id, recordData);
                } else {
                    await createClinicalRecord({ ...recordData, patient: targetPatientId });
                }
            }

            setSaveModal({ show: true, message: 'Cliente guardado correctamente, guardado en la nube', type: 'online' });
            setLoading(false);
        } catch (error) {
            console.error('Error guardando historia clínica:', error);
            
            // Solo es "sin internet" si no hubo respuesta del servidor. Si el servidor respondió con error
            // (datos inválidos, sesión vencida, error 500) se muestra el error real en vez de fingir que se guardó.
            const httpStatus = error.status || error.response?.status;
            if (!navigator.onLine || !httpStatus) {
                const transactionData = {
                    patientData: patientData,
                    patientId: savedPatientId,
                    recordData: recordData,
                    existingQuoteId: clinicalData?.id || null
                };

                const queued = savePendingTransaction('NEW_QUOTE_OR_CLINICAL_RECORD', transactionData);
                setSaveModal(queued
                    ? { show: true, message: 'No hay conexión a internet. Los datos quedaron guardados SOLO en este dispositivo y se enviarán al servidor automáticamente cuando vuelva la conexión. No borre los datos del navegador.', type: 'offline' }
                    : { show: true, message: 'No hay conexión y no se pudo guardar localmente. NO cierre esta ventana; intente guardar de nuevo cuando vuelva la conexión.', type: 'error' });
                setLoading(false);
                return;
            }

            if (httpStatus === 401) {
                setSaveModal({ show: true, message: 'Su sesión expiró. NO cierre esta ventana: inicie sesión en otra pestaña y vuelva a presionar Guardar.', type: 'error' });
                setLoading(false);
                return;
            }

            const errorData = error.data || error.response?.data;
            const errorMsg = errorData ? JSON.stringify(errorData) : error.message;
            setSaveModal({ show: true, message: `Verifique los datos ingresados: ${errorMsg}`, type: 'error' });
            setLoading(false);
        }
    };

    const styles = {
        document: {
            width: '100%',
            maxWidth: '1000px',
            margin: '0 auto',
            backgroundColor: '#fff',
            padding: '40px',
            fontFamily: 'Calibri, sans-serif',
            color: '#000',
            border: '1px solid #ccc',
            boxShadow: '0 0 15px rgba(0,0,0,0.1)',
            fontSize: '10pt'
        },
        input: {
            border: 'none',
            borderBottom: '1px dotted #999',
            outline: 'none',
            backgroundColor: 'transparent',
            width: '100%',
            fontFamily: 'Calibri, sans-serif',
            fontSize: '10pt',
        },
        textarea: {
            border: 'none',
            width: '100%',
            outline: 'none',
            resize: 'none',
            fontFamily: 'Calibri, sans-serif',
            fontSize: '10pt',
            lineHeight: '1.5',
            backgroundColor: 'transparent',
            minHeight: '60px',
            padding: '5px'
        },
        tableBorder: {
            border: '1.5px solid #000',
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: '10px',
            boxSizing: 'border-box'
        },
        cellBorder: {
            border: '1px solid #000',
            padding: '4px 8px',
            verticalAlign: 'middle',
            boxSizing: 'border-box'
        },
        headerTitle: {
            backgroundColor: '#e6e6e6',
            fontWeight: 'bold',
            padding: '2px 8px',
            borderBottom: '1.5px solid #000',
            fontSize: '10pt'
        },
        smallText: {
            fontSize: '5pt',
            color: '#333',
            textAlign: 'right',
            fontFamily: 'Calibri, sans-serif'
        }
    };

    return (
        <form onSubmit={handleSubmit} onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                e.preventDefault();
            }
        }}>
            <Modal 
                isOpen={saveModal.show} 
                onClose={() => { setSaveModal({ ...saveModal, show: false }); if(saveModal.type !== 'error') onSave(); }} 
                title={saveModal.type === 'online' ? 'Guardado Exitoso' : saveModal.type === 'offline' ? 'Guardado Local (Sin Internet)' : 'Atención'}
                skipConfirm={true}
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <i className={`bi ${saveModal.type === 'online' ? 'bi-cloud-check-fill' : saveModal.type === 'offline' ? 'bi-hdd-fill' : 'bi-exclamation-triangle-fill'}`} style={{ fontSize: '3rem', color: saveModal.type === 'online' ? '#10b981' : saveModal.type === 'offline' ? '#f59e0b' : '#ef4444' }}></i>
                    <h4 style={{ marginTop: '15px' }}>{saveModal.message}</h4>
                    {saveModal.type === 'offline' && <p style={{ color: '#666', marginTop: '10px' }}>La información se sincronizará automáticamente en segundo plano cuando vuelva la conexión.</p>}
                    <button type="button" style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', backgroundColor: '#b87a00', color: '#fff', border: 'none', fontWeight: '700', cursor: 'pointer', marginTop: '20px' }} onClick={() => { setSaveModal({ ...saveModal, show: false }); if(saveModal.type !== 'error') onSave(); }}>Entendido</button>
                </div>
            </Modal>

            <div id="documento-impresion" style={styles.document}>
                {/* ENCABEZADO Y LOGO */}
                <div id="logo-container" style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', textAlign: 'center' }} align="center">
                    <img 
                        src="/biodentality.png" 
                        alt="Logo" 
                        style={{ maxWidth: '100%', maxHeight: '95px', objectFit: 'contain' }} 
                    />
                </div>

                {/* PERSONAL INFO SECTION */}
                <table style={styles.tableBorder}>
                    <tbody>
                        <tr>
                            <td style={{ ...styles.cellBorder, width: '40%' }}>
                                <div className="d-flex align-items-center">
                                    <span className="fw-bold me-2" style={{ whiteSpace: 'nowrap' }}>Nombres y Apellidos:</span>
                                    <input 
                                        type="text" 
                                        style={{...styles.input, marginRight: '5px', minWidth: '80px', textTransform: 'capitalize'}} 
                                        name="first_name" 
                                        placeholder="Nombres"
                                        value={formData.first_name} 
                                        onChange={handleChange} 
                                        required 
                                        disabled={isViewOnly}
                                    />
                                    <input 
                                        type="text" 
                                        style={{...styles.input, minWidth: '80px', textTransform: 'capitalize'}} 
                                        name="last_name" 
                                        placeholder="Apellidos"
                                        value={formData.last_name} 
                                        onChange={handleChange} 
                                        required 
                                        disabled={isViewOnly}
                                    />
                                </div>
                            </td>
                            <td style={{ ...styles.cellBorder, width: '25%' }}>
                                <div className="d-flex align-items-center">
                                    <span className="fw-bold me-2" style={{ whiteSpace: 'nowrap' }}>Cédula/HCL:</span>
                                    <input 
                                        type="text" 
                                        style={styles.input} 
                                        name="cedula" 
                                        value={formData.cedula} 
                                        onChange={handleChange} 
                                        disabled={isViewOnly}
                                        maxLength="50"
                                    />
                                </div>
                            </td>
                            <td style={{ ...styles.cellBorder, width: '35%' }}>
                                <div className="d-flex align-items-center flex-wrap justify-content-between">
                                    <div className="d-flex align-items-center me-2">
                                        <span className="fw-bold me-2" style={{ whiteSpace: 'nowrap' }}>Fecha nac.:</span>
                                        <input 
                                            type="date" 
                                            style={styles.input} 
                                            name="birth_date" 
                                            value={formData.birth_date} 
                                            onChange={handleChange} 
                                            disabled={isViewOnly}
                                        />
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <span className="fw-bold me-2" style={{ whiteSpace: 'nowrap' }}>Sexo:</span>
                                        <select 
                                            style={{...styles.input, minWidth: '90px'}} 
                                            name="gender" 
                                            value={formData.gender || ''} 
                                            onChange={handleChange} 
                                            disabled={isViewOnly}
                                        >
                                            <option value="">Seleccione</option>
                                            <option value="M">Masculino</option>
                                            <option value="F">Femenino</option>
                                        </select>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ ...styles.cellBorder, width: '40%' }}>
                                <div className="d-flex align-items-center">
                                    <span className="fw-bold me-2" style={{ whiteSpace: 'nowrap' }}>Representante:</span>
                                    <input 
                                        type="text" 
                                        style={{...styles.input, textTransform: 'capitalize'}} 
                                        name="representative" 
                                        value={formData.representative} 
                                        onChange={handleChange} 
                                        disabled={isViewOnly}
                                    />
                                </div>
                            </td>
                            <td style={{ ...styles.cellBorder, width: '25%' }}>
                                <div className="d-flex align-items-center">
                                    <span className="fw-bold me-2" style={{ whiteSpace: 'nowrap' }}>Cédula Rep.:</span>
                                    <input 
                                        type="text" 
                                        style={styles.input} 
                                        name="representative_cedula" 
                                        value={formData.representative_cedula} 
                                        onChange={handleChange} 
                                        disabled={isViewOnly}
                                        maxLength="50"
                                    />
                                </div>
                            </td>
                            <td style={{ ...styles.cellBorder, width: '35%' }}>
                                <div className="d-flex align-items-center">
                                    <span className="fw-bold me-2" style={{ whiteSpace: 'nowrap' }}>Teléfono:</span>
                                    <input 
                                        type="text" 
                                        style={styles.input} 
                                        name="phone" 
                                        value={formData.phone} 
                                        onChange={handleChange} 
                                        disabled={isViewOnly}
                                        maxLength="50"
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ ...styles.cellBorder, width: '40%' }}>
                                <div className="d-flex align-items-center">
                                    <span className="fw-bold me-2" style={{ whiteSpace: 'nowrap' }}>Dirección:</span>
                                    <input 
                                        type="text" 
                                        style={styles.input} 
                                        name="address" 
                                        value={formData.address || ''} 
                                        onChange={handleChange} 
                                        disabled={isViewOnly}
                                    />
                                </div>
                            </td>
                            <td style={{ ...styles.cellBorder, width: '25%' }}>
                                <div className="d-flex align-items-center">
                                    <span className="fw-bold me-2" style={{ whiteSpace: 'nowrap' }}>Correo:</span>
                                    <input 
                                        type="email" 
                                        style={styles.input} 
                                        name="email" 
                                        value={formData.email || ''} 
                                        onChange={handleChange} 
                                        disabled={isViewOnly}
                                    />
                                </div>
                            </td>
                            <td style={{ ...styles.cellBorder, width: '35%' }}>
                                <div className="d-flex align-items-center">
                                    <span className="fw-bold me-2" style={{ whiteSpace: 'nowrap' }}>Ocupación:</span>
                                    <input 
                                        type="text" 
                                        style={styles.input} 
                                        name="occupation" 
                                        value={formData.occupation} 
                                        onChange={handleChange} 
                                        disabled={isViewOnly}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* AGE GROUPS TABLE */}
                <table style={{ ...styles.tableBorder, textAlign: 'left', fontSize: '7pt', tableLayout: 'fixed', width: '100%' }}>
                    <tbody>
                        <tr>
                            {ageGroups.map(ag => (
                                <React.Fragment key={ag.id}>
                                    <td className="age-group-text" style={{ ...styles.cellBorder, width: ag.width, backgroundColor: formData.age_group === ag.id ? '#e0f0ff' : 'transparent', padding: '4px 8px', verticalAlign: 'middle' }}>
                                        {ag.label}
                                    </td>
                                    <td style={{ ...styles.cellBorder, cursor: isViewOnly ? 'default' : 'pointer', width: '2%', textAlign: 'center', backgroundColor: formData.age_group === ag.id ? '#e0f0ff' : 'transparent' }} onClick={() => !isViewOnly && setFormData(prev => ({...prev, age_group: ag.id}))}>
                                        <input 
                                            type="radio" 
                                            name="age_group" 
                                            value={ag.id} 
                                            checked={formData.age_group === ag.id}
                                            onChange={handleChange}
                                            disabled={isViewOnly}
                                            style={{ margin: 0 }}
                                        />
                                    </td>
                                </React.Fragment>
                            ))}
                            <td className="age-group-text" style={{ ...styles.cellBorder, width: '9%', padding: '4px 8px', verticalAlign: 'middle', backgroundColor: formData.is_pregnant ? '#ffe0e0' : 'transparent' }}>
                                EMBARAZADA
                            </td>
                            <td style={{ ...styles.cellBorder, cursor: isViewOnly ? 'default' : 'pointer', width: '2%', textAlign: 'center', backgroundColor: formData.is_pregnant ? '#ffe0e0' : 'transparent' }} onClick={() => !isViewOnly && setFormData(prev => ({...prev, is_pregnant: !prev.is_pregnant}))}>
                                <input 
                                    type="checkbox" 
                                    name="is_pregnant" 
                                    checked={formData.is_pregnant}
                                    onChange={handleChange}
                                    disabled={isViewOnly}
                                    style={{ margin: 0 }}
                                />
                            </td>
                            <td className="age-group-text" style={{ ...styles.cellBorder, width: '9%', padding: '4px 8px', verticalAlign: 'middle', backgroundColor: formData.has_disability ? '#e0f0ff' : 'transparent' }}>
                                DISCAPACIDAD
                                {formData.has_disability && (
                                    <input 
                                        type="text"
                                        name="disability_description"
                                        value={formData.disability_description}
                                        onChange={handleChange}
                                        placeholder="¿Cuál?"
                                        disabled={isViewOnly}
                                        style={{...styles.input, width: '80px', marginLeft: '5px', fontSize: '5pt', padding: 0}}
                                    />
                                )}
                            </td>
                            <td style={{ ...styles.cellBorder, cursor: isViewOnly ? 'default' : 'pointer', width: '20px', textAlign: 'center', backgroundColor: formData.has_disability ? '#e0f0ff' : 'transparent' }} onClick={() => !isViewOnly && setFormData(prev => ({...prev, has_disability: !prev.has_disability}))}>
                                <input 
                                    type="checkbox" 
                                    name="has_disability" 
                                    checked={formData.has_disability}
                                    onChange={handleChange}
                                    disabled={isViewOnly}
                                    style={{ margin: 0 }}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* 1. MOTIVO DE CONSULTA */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                    <tbody>
                        <tr>
                            <td style={{ border: '1.5px solid #000', padding: 0 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', fontWeight: 'bold', fontSize: '10pt', textAlign: 'left', width: '50%' }}>
                                                1 MOTIVO DE CONSULTA
                                            </td>
                                            <td style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', textAlign: 'right', fontSize: '6pt', color: '#333', width: '50%' }}>
                                                ANOTAR LA CAUSA DEL PROBLEMA EN LA VERSIÓN DEL INFORMANTE
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div style={{ padding: '0 5px' }}>
                                    <textarea 
                                        style={styles.textarea} 
                                        name="reason_for_consultation"
                                        value={formData.reason_for_consultation}
                                        onChange={handleChange}
                                        onBlur={handleMotivoBlur}
                                        placeholder="Escriba aquí el motivo de consulta..."
                                        disabled={isViewOnly}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* 2. ENFERMEDAD O PROBLEMA ACTUAL */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                    <tbody>
                        <tr>
                            <td style={{ border: '1.5px solid #000', padding: 0 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', fontWeight: 'bold', fontSize: '10pt', textAlign: 'left', width: '40%' }}>
                                                2 ENFERMEDAD O PROBLEMA ACTUAL
                                            </td>
                                            <td style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', textAlign: 'right', fontSize: '6pt', color: '#333', width: '60%' }}>
                                                REGISTRAR SÍNTOMAS: CRONOLOGÍA, LOCALIZACIÓN, CARACTERÍSTICAS, INTENSIDAD, CAUSA APARENTE, SÍNTOMAS ASOCIADOS, EVOLUCIÓN, ESTADO ACTUAL
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div style={{ padding: '0 5px' }}>
                                    <textarea 
                                        style={{ ...styles.textarea, minHeight: '60px' }} 
                                        name="current_illness"
                                        value={formData.current_illness}
                                        onChange={handleChange}
                                        placeholder="Describa la enfermedad o problema actual..."
                                        disabled={isViewOnly}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* 3. ANTECEDENTES PERSONALES Y FAMILIARES */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                    <tbody>
                        <tr>
                            <td style={{ border: '1.5px solid #000', padding: 0 }}>
                                <div style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', fontWeight: 'bold', fontSize: '10pt', textAlign: 'left' }}>
                                    3 ANTECEDENTES PERSONALES Y FAMILIARES
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '6pt', margin: 0, tableLayout: 'fixed' }}>
                                    <tbody>
                                        <tr>
                                            {[
                                                {id: 'allergies_antibiotic', label: '1. ALERGIA ANTIBIÓTICO'},
                                                {id: 'allergies_anesthesia', label: '2. ALERGIA ANESTESIA'},
                                                {id: 'hemorrhages', label: '3. HEMORRAGIAS'},
                                                {id: 'hiv_aids', label: '4. VIH/SIDA'},
                                                {id: 'tuberculosis', label: '5. TUBERCULOSIS'},
                                                {id: 'asthma', label: '6. ASMA'},
                                                {id: 'diabetes', label: '7. DIABETES'},
                                                {id: 'hypertension', label: '8. HIPERTENSIÓN'},
                                                {id: 'cardiac_disease', label: '9. ENF. CARDIACA'},
                                                {id: 'other_history', label: '10. OTRO'}
                                            ].map((item, idx) => (
                                                <React.Fragment key={item.id}>
                                                    <td 
                                                        className="age-group-text" 
                                                        style={{ border: '1px solid #000', padding: '4px 8px', verticalAlign: 'middle', width: '7%', backgroundColor: formData[item.id] ? '#e0f0ff' : 'transparent', cursor: 'grab' }}
                                                        draggable={!isViewOnly}
                                                        onDragStart={(e) => {
                                                            let text = item.label.split('. ')[1] || item.label;
                                                            if (item.id === 'other_history' && formData.other_history_notes) {
                                                                text = formData.other_history_notes;
                                                            }
                                                            e.dataTransfer.setData('text/plain', text);
                                                        }}
                                                        title="Arrastra este texto a AP o AF"
                                                    >
                                                        {item.label}
                                                        {item.id === 'other_history' && formData.other_history && (
                                                            <div style={{ marginTop: '2px' }}>
                                                                <input
                                                                    type="text"
                                                                    name="other_history_notes"
                                                                    value={formData.other_history_notes}
                                                                    onChange={handleChange}
                                                                    placeholder="Especifique..."
                                                                    style={{ width: '100%', fontSize: '5pt', padding: '1px' }}
                                                                    disabled={isViewOnly}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ border: '1px solid #000', padding: '4px 8px', verticalAlign: 'middle', cursor: isViewOnly ? 'default' : 'pointer', width: '3%', textAlign: 'center', backgroundColor: formData[item.id] ? '#e0f0ff' : 'transparent' }} onClick={() => !isViewOnly && setFormData(prev => ({...prev, [item.id]: !prev[item.id]}))}>
                                                        <input 
                                                            type="checkbox" 
                                                            name={item.id}
                                                            checked={formData[item.id]}
                                                            onChange={handleChange}
                                                            disabled={isViewOnly}
                                                            style={{ margin: 0 }}
                                                        />
                                                    </td>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                                <div style={{ padding: '5px', borderBottom: '1px solid #ccc', display: 'flex', flexDirection: 'column' }}>
                                    <div className="fw-bold mb-1">AP:</div>
                                    <textarea 
                                        style={{ ...styles.textarea, minHeight: '30px' }} 
                                        name="personal_history_notes"
                                        value={formData.personal_history_notes}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const text = e.dataTransfer.getData('text/plain');
                                            if (text) {
                                                setFormData(prev => {
                                                    const current = prev.personal_history_notes;
                                                    const sep = current ? ', ' : '';
                                                    return { ...prev, personal_history_notes: current + sep + text };
                                                });
                                            }
                                        }}
                                    />
                                </div>
                                <div style={{ padding: '5px', display: 'flex', flexDirection: 'column' }}>
                                    <div className="fw-bold mb-1">AF:</div>
                                    <textarea 
                                        style={{ ...styles.textarea, minHeight: '30px' }} 
                                        name="family_history_notes"
                                        value={formData.family_history_notes}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const text = e.dataTransfer.getData('text/plain');
                                            if (text) {
                                                setFormData(prev => {
                                                    const current = prev.family_history_notes;
                                                    const sep = current ? ', ' : '';
                                                    return { ...prev, family_history_notes: current + sep + text };
                                                });
                                            }
                                        }}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* 4. SIGNOS VITALES */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
                    <tbody>
                        <tr>
                            <td style={{ border: '1.5px solid #000', borderTop: 'none', padding: 0 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', fontWeight: 'bold', fontSize: '10pt', textAlign: 'left', width: '30%' }}>
                                                4 SIGNOS VITALES
                                            </td>
                                            <td style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', textAlign: 'right', fontSize: '5pt', color: '#333', width: '70%' }}>
                                                ANOTAR LA PRESIÓN ARTERIAL, FRECUENCIA CARDÍACA, TEMPERATURA Y FRECUENCIA RESPIRATORIA
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', fontSize: '8pt' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ border: '1px solid #000', padding: '4px 8px', width: '16.6%', borderLeft: 'none', borderBottom: 'none' }}>
                                                <div className="fw-bold mb-1">PRESIÓN ARTERIAL</div>
                                                <input 
                                                    type="text" 
                                                    style={{...styles.input, textAlign: 'center'}}
                                                    name="blood_pressure"
                                                    value={formData.blood_pressure}
                                                    onChange={handleChange}
                                                    disabled={isViewOnly}
                                                />
                                            </td>
                                            <td style={{ border: '1px solid #000', padding: '4px 8px', width: '16.6%', borderBottom: 'none' }}>
                                                <div className="fw-bold mb-1">FREC. CARDÍACA</div>
                                                <input 
                                                    type="text" 
                                                    style={{...styles.input, textAlign: 'center'}}
                                                    name="heart_rate"
                                                    value={formData.heart_rate}
                                                    onChange={handleChange}
                                                    disabled={isViewOnly}
                                                />
                                            </td>
                                            <td style={{ border: '1px solid #000', padding: '4px 8px', width: '16.6%', borderBottom: 'none' }}>
                                                <div className="fw-bold mb-1">TEMPERATURA °C</div>
                                                <input 
                                                    type="text" 
                                                    style={{...styles.input, textAlign: 'center'}}
                                                    name="temperature"
                                                    value={formData.temperature}
                                                    onChange={handleChange}
                                                    disabled={isViewOnly}
                                                />
                                            </td>
                                            <td style={{ border: '1px solid #000', padding: '4px 8px', width: '16.6%', borderBottom: 'none' }}>
                                                <div className="fw-bold mb-1">F. RESPIRAT.</div>
                                                <input 
                                                    type="text" 
                                                    style={{...styles.input, textAlign: 'center'}}
                                                    name="respiratory_rate"
                                                    value={formData.respiratory_rate}
                                                    onChange={handleChange}
                                                    disabled={isViewOnly}
                                                />
                                            </td>
                                            <td style={{ border: '1px solid #000', padding: '4px 8px', width: '16.6%', borderBottom: 'none' }}>
                                                <div className="fw-bold mb-1">PESO (kg)</div>
                                                <input 
                                                    type="text" 
                                                    style={{...styles.input, textAlign: 'center'}}
                                                    name="weight"
                                                    value={formData.weight}
                                                    onChange={handleChange}
                                                    disabled={isViewOnly}
                                                />
                                            </td>
                                            <td style={{ border: '1px solid #000', padding: '4px 8px', width: '16.6%', borderRight: 'none', borderBottom: 'none' }}>
                                                <div className="fw-bold mb-1">TALLA (cm)</div>
                                                <input 
                                                    type="text" 
                                                    style={{...styles.input, textAlign: 'center'}}
                                                    name="height"
                                                    value={formData.height}
                                                    onChange={handleChange}
                                                    disabled={isViewOnly}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* 5. EXAMEN DEL SISTEMA ESTOMATOGNÁTICO */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
                    <tbody>
                        <tr>
                            <td style={{ border: '1.5px solid #000', borderTop: 'none', padding: 0 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', fontWeight: 'bold', fontSize: '10pt', textAlign: 'left', width: '50%' }}>
                                                5 EXAMEN DEL SISTEMA ESTOMATOGNÁTICO
                                            </td>
                                            <td style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', textAlign: 'right', fontSize: '5pt', color: '#333', width: '50%' }}>
                                                DESCRIBIR ABAJO LA PATOLOGÍA DE LA REGIÓN AFECTADA ANOTANDO EL NUMERO
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '5pt', margin: 0 }}>
                                    <tbody>
                                        <tr>
                                            {[
                                                {id: 'stomatognathic_lips', label: '1. LABIOS'},
                                                {id: 'stomatognathic_cheeks', label: '2. MEJILLAS'},
                                                {id: 'stomatognathic_upper_jaw', label: '3. MAXILAR SUPERIOR'},
                                                {id: 'stomatognathic_lower_jaw', label: '4. MAXILAR INFERIOR'},
                                                {id: 'stomatognathic_tongue', label: '5. LENGUA'},
                                                {id: 'stomatognathic_palate', label: '6. PALADAR'},
                                                {id: 'stomatognathic_floor', label: '7. PISO'},
                                                {id: 'stomatognathic_carrillos', label: '8. CARRILLOS'}
                                            ].map((item, idx) => (
                                                <React.Fragment key={item.id}>
                                                    <td 
                                                        className="age-group-text" 
                                                        style={{ border: '1px solid #000', padding: '2px 4px', verticalAlign: 'middle', width: 'auto', whiteSpace: 'nowrap', backgroundColor: formData[item.id] ? '#e0f0ff' : 'transparent', borderTop: 'none', borderLeft: idx === 0 ? 'none' : '1px solid #000' }}
                                                    >
                                                        {item.label}
                                                    </td>
                                                    <td style={{ border: '1px solid #000', padding: '2px 4px', verticalAlign: 'middle', cursor: isViewOnly ? 'default' : 'pointer', width: '15px', textAlign: 'center', backgroundColor: formData[item.id] ? '#e0f0ff' : 'transparent', borderTop: 'none', borderRight: idx === 7 ? 'none' : '1px solid #000' }} onClick={() => !isViewOnly && setFormData(prev => ({...prev, [item.id]: !prev[item.id]}))}>
                                                        <input 
                                                            type="checkbox" 
                                                            name={item.id}
                                                            checked={formData[item.id]}
                                                            onChange={handleChange}
                                                            disabled={isViewOnly}
                                                            style={{ margin: 0 }}
                                                        />
                                                    </td>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                        <tr>
                                            {[
                                                {id: 'stomatognathic_salivary_glands', label: '9. GLÁNDULAS SALIVALES'},
                                                {id: 'stomatognathic_oropharynx', label: '10. ORO FARINGE'},
                                                {id: 'stomatognathic_tmj', label: '11. A. T. M.'},
                                                {id: 'stomatognathic_lymph_nodes', label: '12. GANGLIOS'}
                                            ].map((item, idx) => (
                                                <React.Fragment key={item.id}>
                                                    <td 
                                                        className="age-group-text" 
                                                        style={{ border: '1px solid #000', padding: '2px 4px', verticalAlign: 'middle', width: 'auto', whiteSpace: 'nowrap', backgroundColor: formData[item.id] ? '#e0f0ff' : 'transparent', borderBottom: 'none', borderLeft: idx === 0 ? 'none' : '1px solid #000' }}
                                                    >
                                                        {item.label}
                                                    </td>
                                                    <td style={{ border: '1px solid #000', padding: '2px 4px', verticalAlign: 'middle', cursor: isViewOnly ? 'default' : 'pointer', width: '15px', textAlign: 'center', backgroundColor: formData[item.id] ? '#e0f0ff' : 'transparent', borderBottom: 'none' }} onClick={() => !isViewOnly && setFormData(prev => ({...prev, [item.id]: !prev[item.id]}))}>
                                                        <input 
                                                            type="checkbox" 
                                                            name={item.id}
                                                            checked={formData[item.id]}
                                                            onChange={handleChange}
                                                            disabled={isViewOnly}
                                                            style={{ margin: 0 }}
                                                        />
                                                    </td>
                                                </React.Fragment>
                                            ))}
                                            {/* Fill remaining empty cells since second row only has 4 items (8 columns total) and first row has 8 items (16 columns total) */}
                                            <td style={{ border: '1px solid #000', borderBottom: 'none', borderRight: 'none' }} colSpan={8}></td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div style={{ borderTop: '1px solid #000', padding: '0 5px' }}>
                                    <textarea 
                                        style={{ ...styles.textarea, minHeight: '40px' }} 
                                        name="stomatognathic_notes"
                                        value={formData.stomatognathic_notes}
                                        onChange={handleChange}
                                        disabled={isViewOnly}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* 6. ODONTOGRAMA */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
                    <tbody>
                        <tr>
                            <td style={{ border: '1.5px solid #000', borderTop: 'none', padding: 0 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', fontWeight: 'bold', fontSize: '10pt', textAlign: 'left', width: '50%' }}>
                                                6 ODONTOGRAMA
                                            </td>
                                            <td style={{ backgroundColor: '#e6e6e6', padding: '2px 8px', borderBottom: '1.5px solid #000', textAlign: 'right', fontSize: '5pt', color: '#333', width: '50%' }}>
                                                PINTAR CON: <span style={{color: 'blue'}}>AZUL</span> PARA TRATAMIENTO REALIZADO - <span style={{color: 'red'}}>ROJO</span> PARA PATOLOGIA ACTUAL<br/>
                                                MOVILIDAD Y RECESIÓN: MARCAR "X" (1, 2 ó 3), SI APLICA
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div style={{ padding: '10px', overflowX: 'auto' }} id="odontogram-export-container" className="avoid-break">
                                    <Odontograma 
                                        data={formData.odontogram_data} 
                                        onChange={(newData) => setFormData(prev => ({...prev, odontogram_data: newData}))} 
                                        isViewOnly={isViewOnly} 
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <br clear="all" style={{ pageBreakBefore: 'always' }} className="d-print-page-break" />

                {/* 7, 8, 9 */}
                <Fase789 
                    oralHealthData={formData.oral_health_indicators}
                    onOralHealthChange={(newData) => setFormData(prev => ({...prev, oral_health_indicators: newData}))}
                    periodontalDisease={formData.periodontal_disease}
                    onPeriodontalChange={(newData) => setFormData(prev => ({...prev, periodontal_disease: newData}))}
                    malocclusion={formData.malocclusion}
                    onMalocclusionChange={(newData) => setFormData(prev => ({...prev, malocclusion: newData}))}
                    fluorosis={formData.fluorosis}
                    onFluorosisChange={(newData) => setFormData(prev => ({...prev, fluorosis: newData}))}
                    cpoCeoData={formData.cpo_ceo_indices}
                    onCpoCeoChange={(newData) => setFormData(prev => ({...prev, cpo_ceo_indices: newData}))}
                    isViewOnly={isViewOnly}
                />

                {/* 10, 11 */}
                <Fase1011 
                    isViewOnly={isViewOnly} 
                    formData={formData} 
                    setFormData={setFormData} 
                />

                {/* 12 */}
                {/* 12 */}
                <Fase12 
                    isViewOnly={isViewOnly} 
                    formData={formData} 
                    setFormData={setFormData} 
                />

                {/* CONTROLS */}
                <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top d-print-none">
                    <button type="button" className="btn btn-info text-white px-4 shadow-sm" onClick={exportToWord} title="Exportar a Word">
                        <i className="bi bi-file-word-fill me-2"></i>Exportar a Word
                    </button>
                    <button type="button" className="btn btn-secondary px-4 shadow-sm" onClick={onCancel} disabled={loading}>
                        {isViewOnly ? 'Cerrar' : 'Cancelar'}
                    </button>
                    {!isViewOnly && (
                        <button type="submit" className="btn btn-success px-4 shadow-sm" disabled={loading}>
                            {loading ? 'Guardando...' : <><i className="bi bi-save-fill me-2"></i>Guardar y Generar Registro</>}
                        </button>
                    )}
                </div>
            </div>
        </form>
    );
};

export default FormularioPaciente;
